import { Link, TableCell } from '@mui/material';
import React from 'react';

import { hostForCurrentEnv, platform360HostMap } from '../../constants/config';

export type SsoEntityKind = 'organization' | 'advertiser' | 'user';

interface SsoIdLinkProps {
  kind: SsoEntityKind;
  ssoId: unknown;
  // Unused for 'organization'.
  orgSsoId?: unknown;
}

interface SsoIdCellProps {
  kind: SsoEntityKind;
  entity: unknown;
  // Falls back to the entity's nested `client`. Unused for 'organization'.
  orgSsoId?: unknown;
}

const isPresentSsoId = (id: unknown): boolean => {
  if (id == null || id === '') return false;
  const n = Number(id);
  return Number.isFinite(n) && n !== 0;
};

// Index payloads are snake_case on Rails but camelCase through some ACH paths.
const readSsoId = (entity: unknown): unknown => {
  if (entity == null || typeof entity !== 'object') return undefined;
  const record = entity as Record<string, unknown>;
  return record.sso_id ?? record.ssoId;
};

const ssoEntityHref = (kind: SsoEntityKind, ids: { ssoId?: unknown; orgSsoId?: unknown }): string | null => {
  const base = hostForCurrentEnv(platform360HostMap);
  if (kind === 'organization') {
    if (!isPresentSsoId(ids.ssoId)) return null;
    return `${base}/userManagement/organizations/${ids.ssoId}`;
  }
  if (!isPresentSsoId(ids.orgSsoId) || !isPresentSsoId(ids.ssoId)) return null;
  if (kind === 'advertiser') {
    return `${base}/userManagement/organizations/${ids.orgSsoId}/advertisers/${ids.ssoId}`;
  }
  return `${base}/userManagement/organizations/${ids.orgSsoId}/users/${ids.ssoId}`;
};

// N/A means no SSO ID. An id whose deep link cannot be built (unknown parent
// org) still renders, as plain text.
export function SsoIdLink({ kind, ssoId, orgSsoId }: SsoIdLinkProps) {
  if (!isPresentSsoId(ssoId)) {
    return <>N/A</>;
  }

  const href = ssoEntityHref(kind, { ssoId, orgSsoId });

  if (!href) {
    return <>{String(ssoId)}</>;
  }

  return (
    <Link href={href} target="_blank" rel="noopener noreferrer" underline="hover">
      {String(ssoId)}
    </Link>
  );
}

function SsoIdCell({ kind, entity, orgSsoId }: SsoIdCellProps) {
  const ssoId = readSsoId(entity);
  const parentSsoId = orgSsoId ?? readSsoId((entity as { client?: unknown })?.client);

  return (
    <TableCell align="left" onClick={(event) => event.stopPropagation()}>
      <SsoIdLink kind={kind} ssoId={ssoId} orgSsoId={parentSsoId} />
    </TableCell>
  );
}

export default SsoIdCell;
